import { useEffect, useRef, useState } from 'react';
import type { AdPolicy } from '@apm/contracts';
import { useAdsEnabled } from '../ads/ads-enabled.js';
import { fetchSeriesEpisodes } from '../catalog-client.js';
import { episodeEntryFocus, toEpisodeRows } from '../content-sheet.js';
import type { ContentRow, ContentSheetMode } from '../content-sheet.js';
import type { ProductListRow } from '../product-list.js';
import { ProductListScreen } from '../screens/ProductListScreen.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 「전체 상품」 시트 (v11, `docs/27` §4).
 *
 * **채널 시트와 같은 층이다** (`.sheet` — `inset: 0` · z-31 · 70% 스크림). 영상은 축소되지
 * 않고 **전체 크기로 계속 재생되며**(절대 규칙 14) 목록이 그 위를 덮는다.
 *
 * ── v11 은 미니 플레이어 배치였다 (2026-08-10 개정) ──────────
 *
 * 처음에는 상품 시트(`.product-sheet`)와 같은 자리에 세웠다. 근거는 **모바일**이었다 —
 * 목록에서 골라 구매로 이어질 때 두 시트가 같은 기하여야 영상 크기가 그대로 이어진다.
 *
 * **TV 에서는 그 근거가 성립하지 않는다.** TV·PC 의 구매는 상품 시트가 아니라 Push/QR
 * 오버레이로 가므로 「크기가 이어진다」가 지킬 대상이 없고, 10-foot UI 에서는 방송을 전체
 * 크기로 둔 채 목록을 덮는 편이 낫다. 배치를 한 벌로 통일해 CSS·하네스·문서가 각각 하나로
 * 남는다 — 대가는 모바일에서 목록→구매 사이에 영상이 한 번 축소되는 것뿐이다.
 */
export function ProductListSheet({
  rows,
  isGeneral = false,
  resume,
  adPolicy,
  onPick,
  onClose,
  content,
}: {
  rows: readonly ProductListRow[];
  /** 이어볼 지점 (v18). 그 회차 한 행에만 표시가 붙는다 */
  resume?: { productId: string; positionSec: number } | null;
  /**
   * 일반 채널 모드 (v16). **시트를 복제하지 않고 문구만 가른다** — 셸·포커스 스코프·
   * 닫기 버튼·스크림 규칙이 같고 다른 것은 행의 내용뿐이다 (`docs/38` §6.4).
   */
  isGeneral?: boolean;
  /**
   * v21. 이 채널의 광고 정책. **`null` 이면 배지가 없다.**
   */
  adPolicy?: AdPolicy | null;
  onPick: (productId: string) => void;
  onClose: () => void;
  /**
   * v39 「콘텐츠」 2단계 (`docs/81` §5.1 · 절대 규칙 106). **`mode` 가 `'flat'` 이거나 없으면 지금의 시트 그대로다**(D8) —
   * 고를 것이 하나뿐인 채널은 한 픽셀도 안 바뀐다.
   */
  content?: {
    mode: ContentSheetMode;
    channelId: string;
    rows: readonly ContentRow[];
    currentProductId: string | null;
    /** 채널을 틀 때의 카탈로그가 실패했다 — 시트 머리에 한 줄로 말한다(조용하지 않게) */
    catalogFailed: boolean;
  };
}) {
  if (isGeneral && content?.mode === 'series') {
    return (
      <ContentLevels
        content={content}
        resume={resume ?? null}
        adPolicy={adPolicy ?? null}
        onPick={onPick}
        onClose={onClose}
      />
    );
  }
  return (
    <FlatSheet
      rows={rows}
      isGeneral={isGeneral}
      resume={resume}
      adPolicy={adPolicy}
      onPick={onPick}
      onClose={onClose}
      catalogFailed={isGeneral && content?.catalogFailed === true}
    />
  );
}

/** 지금의 시트(v11 · v16) — `'flat'` 과 홈쇼핑. 카탈로그가 실패했으면 머리에 한 줄을 더한다 */
function FlatSheet({
  rows,
  isGeneral,
  resume,
  adPolicy,
  onPick,
  onClose,
  catalogFailed,
}: {
  rows: readonly ProductListRow[];
  isGeneral: boolean;
  resume?: { productId: string; positionSec: number } | null;
  adPolicy?: AdPolicy | null;
  onPick: (productId: string) => void;
  onClose: () => void;
  catalogFailed: boolean;
}) {
  const t = useT();
  /*
   * ★ **배지가 토글을 본다** (v21). 끈 채로 「+광고 6분」만 남으면 **없는 시간을 말하는
   * 화면**이 된다 — 시청자가 그 값을 믿고 「이 회차는 48분」으로 계획한다.
   */
  const [adsOn] = useAdsEnabled();

  return (
    /*
      `data-focus-scope` — 리모컨 포커스를 이 시트 안에 가둔다 (절대 규칙 32).
      **표시가 렌더하는 자리에 있어야 요소가 옮겨 다녀도 따라간다** (`data-i18n` 과 같은
      방침). 이것 하나로 `verify-tv-focus` 의 층 검사 넷이 자동으로 이 시트를 본다.
    */
    <div
      className="sheet"
      role="dialog"
      aria-label={isGeneral ? t('episodes.title') : t('products.title')}
      data-focus-scope
    >
      <div className="sheet__panel">
        <div className="sheet__head">
          <h2 className="sheet__title">{isGeneral ? t('episodes.title') : t('products.title')}</h2>
          {/* `data-focus-back` — 뒤로 키가 누를 대상. **한 층에 하나여야 한다** */}
          <button
            type="button"
            className="sheet__close"
            data-testid="sheet-close"
            data-focus-back
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>

        <p className="sheet__hint">{isGeneral ? t('episodes.hint') : t('products.hint')}</p>
        {catalogFailed && (
          <p className="sheet__hint sheet__hint--warn" data-testid="catalog-failed">
            {t('contents.loadFailed')}
          </p>
        )}

        <ProductListScreen
          rows={rows}
          onPick={onPick}
          resume={resume}
          adPolicy={adsOn ? (adPolicy ?? null) : null}
        />
      </div>
    </div>
  );
}

/**
 * v39 — 시리즈 → 회차 2단계. **한 시트 · 한 층**(`data-focus-scope` 하나)이고 단계는 그 안의 상태다(규칙 106).
 *
 * - 1단계의 순서는 `toContentRows` 가 정한 그대로다 — 여기서 다시 정렬하지 않는다.
 * - 2단계는 들어갈 때마다 받는다(`fetchSeriesEpisodes`). 행은 지금의 회차 행(`ProductListScreen`)이고 고르면 `onPick`
 *   이다 — 편성 밖 회차여도 같은 문(`selectItem`)이다(규칙 104).
 * - 회차가 하나뿐인 시리즈(단편 · D6)는 2단계를 그리지 않고 곧바로 고른다.
 * - 「뒤로」(`data-focus-back`)는 1단계에서는 닫기, 2단계에서는 1단계로 — **한 층에 하나**다.
 * - 단계가 바뀌면 다음 프레임에 포커스를 옮긴다(`data-content-entry`) — 누른 줄이 사라져 `<body>` 로 떨어지면
 *   「리모컨이 죽었다」와 구분되지 않는다(규칙 32 · `HomeScreen` 의 방식).
 */
function ContentLevels({
  content,
  resume,
  adPolicy,
  onPick,
  onClose,
}: {
  content: NonNullable<Parameters<typeof ProductListSheet>[0]['content']>;
  resume: { productId: string; positionSec: number } | null;
  adPolicy: AdPolicy | null;
  onPick: (productId: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [adsOn] = useAdsEnabled();
  const panelRef = useRef<HTMLDivElement>(null);
  /** `null` 이면 1단계 */
  const [opened, setOpened] = useState<{
    seriesId: string;
    title: string;
    rows: ProductListRow[] | null;
    failed: boolean;
  } | null>(null);
  /** 1단계로 돌아올 때 포커스가 앉을 줄 — 방금 연 시리즈 */
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  // 단계가 바뀌면(그리고 2단계의 회차가 오면) 다음 프레임에 포커스를 옮긴다.
  const stageKey =
    opened === null
      ? `l1:${returnTo ?? ''}`
      : `l2:${opened.seriesId}:${String(opened.rows?.length ?? -1)}`;
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const target = panelRef.current?.querySelector<HTMLElement>('[data-content-entry="true"]');
      if (target !== null && target !== undefined && document.activeElement !== target) {
        target.focus({ preventScroll: false });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [stageKey]);

  const openSeries = (row: Extract<ContentRow, { kind: 'series' }>) => {
    setReturnTo(row.seriesId);
    setPending(row.seriesId);
    void fetchSeriesEpisodes(content.channelId, row.seriesId).then((res) => {
      setPending(null);
      const episodes = res === null ? null : toEpisodeRows(res.episodes, content.currentProductId);
      // 단편(회차 하나) — 2단계 없이 곧바로 고른다(D6)
      if (episodes !== null && episodes.length === 1 && episodes[0] !== undefined) {
        onPick(episodes[0].productId);
        return;
      }
      setOpened({
        seriesId: row.seriesId,
        title: row.title,
        rows: episodes,
        failed: episodes === null,
      });
    });
  };

  const entryEpisode =
    opened?.rows == null ? null : episodeEntryFocus(opened.rows, resume?.productId ?? null);

  return (
    <div className="sheet" role="dialog" aria-label={t('contents.title')} data-focus-scope>
      <div className="sheet__panel" ref={panelRef}>
        <div className="sheet__head">
          {opened === null ? (
            <h2 className="sheet__title">{t('contents.title')}</h2>
          ) : (
            <button
              type="button"
              className="sheet__back"
              data-testid="content-back"
              data-focus-back
              onClick={() => setOpened(null)}
            >
              {'‹ '}
              {t('contents.back')}
            </button>
          )}
          <button
            type="button"
            className="sheet__close"
            data-testid="sheet-close"
            {...(opened === null ? { 'data-focus-back': true } : {})}
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>

        {opened === null ? (
          <>
            <p className="sheet__hint">{t('contents.hint')}</p>
            <ul className="products" data-testid="content-list">
              {content.rows.map((row) =>
                row.kind === 'series' ? (
                  <li key={`s:${row.seriesId}`}>
                    <button
                      type="button"
                      className="products__item"
                      data-testid="content-series"
                      data-series-id={row.seriesId}
                      aria-current={row.current ? 'true' : undefined}
                      aria-busy={pending === row.seriesId ? 'true' : undefined}
                      data-content-entry={
                        (returnTo === null ? row.current : returnTo === row.seriesId)
                          ? 'true'
                          : undefined
                      }
                      onClick={() => openSeries(row)}
                    >
                      <img className="products__thumb" src={row.thumbnailUrl} alt="" />
                      <span className="products__body">
                        <span className="products__category">
                          {t('contents.episodeCount', { n: row.episodeCount })}
                          {' · '}
                          {row.inPlaylist ? t('contents.scheduled') : t('contents.library')}
                        </span>
                        <span className="products__name" data-i18n="data">
                          {row.title}
                        </span>
                      </span>
                      {row.current && (
                        <span className="products__now">{t('contents.watching')}</span>
                      )}
                    </button>
                  </li>
                ) : (
                  <li key={`i:${row.row.productId}`}>
                    <button
                      type="button"
                      className="products__item"
                      data-testid="product-item"
                      data-product-id={row.row.productId}
                      aria-current={row.row.current ? 'true' : undefined}
                      data-content-entry={returnTo === null && row.row.current ? 'true' : undefined}
                      onClick={() => onPick(row.row.productId)}
                    >
                      <img className="products__thumb" src={row.row.thumbnailUrl} alt="" />
                      <span className="products__body">
                        <span className="products__name" data-i18n="data">
                          {row.row.title}
                        </span>
                      </span>
                      {row.row.current && (
                        <span className="products__now">{t('products.nowPlaying')}</span>
                      )}
                    </button>
                  </li>
                ),
              )}
            </ul>
          </>
        ) : (
          <>
            <p className="sheet__hint" data-i18n="data">
              {opened.title}
            </p>
            <p className="sheet__hint">{t('episodes.hint')}</p>
            {opened.failed ? (
              <p className="sheet__hint sheet__hint--warn" data-testid="episodes-failed">
                {t('contents.episodesFailed')}
              </p>
            ) : (
              opened.rows !== null && (
                <ProductListScreen
                  rows={opened.rows}
                  onPick={onPick}
                  resume={resume}
                  adPolicy={adsOn ? adPolicy : null}
                  entryProductId={entryEpisode}
                />
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
